/**
 * setupValidation(formElement, fields, recaptchaId)
 * -------------------------------------------------
 * A lightweight, dependency-free form validation helper.
 * Handles inline errors, field matching, password rules,
 * checkbox requirements, and optional reCAPTCHA integration.
 *
 * USAGE:
 *   import { setupValidation } from './setupValidation.js';
 *
 *   const form = document.querySelector('#signup-form');
 *   setupValidation(form, [
 *     {
 *       selector: 'input[name="email"]',
 *       tests: { required: true, email: true }
 *     },
 *     {
 *       selector: 'input[name="email_confirm"]',
 *       tests: { required: true, emailMatch: 'input[name="email"]' }
 *     },
 *     {
 *       selector: 'input[name="alias"]',
 *       tests: { required: true, aliasLength: true }
 *     },
 *     {
 *       selector: 'input[name="password"]',
 *       tests: { required: true, pwLength: true, pwStrength: true }
 *     },
 *     {
 *       selector: 'input[name="password_confirm"]',
 *       tests: { required: true, pwMatch: 'input[name="password"]' }
 *     },
 *     {
 *       selector: 'input[name="accept_tos"]',
 *       tests: { required: true }
 *     }
 *   ], 'signup');
 *
 * PARAMETERS:
 *   formElement  - The <form> DOM node to validate.
 *   fields       - Array of field configs:
 *                    {
 *                      selector: 'input[name="foo"]',
 *                      tests: {
 *                        required: true,     // non-empty or checked
 *                        email: true,        // matches basic email regex
 *                        emailMatch: '...',  // matches another field
 *                        aliasLength: true,  // >= 4 chars
 *                        pwLength: true,     // 8–40 chars
 *                        pwMatch: '...',     // matches another field
 *                        pwStrength: true    // 3 of 4: upper/lower/digit/symbol
 *                      }
 *                    }
 *   recaptchaId  - String identifying which reCAPTCHA action to run.
 *
 * BEHAVIOR:
 *   • Validates on “change” and on form submit.
 *   • Inline <span class="AuthRow-error"> messages auto-inserted.
 *   • Error messages pulled from window.PageData.errors[errorKey].
 *   • Prevents double-submit by applying .is-submitted to the form.
 *   • Disables all .uses-recaptcha elements before submit.
 *   • If recaptchaId is supplied, calls fetchRecaptchaToken(recaptchaId)
 *     then auto-submits the form.
 *
 * REQUIREMENTS:
 *   • window.PageData.errors must contain keys for each test type.
 *   • fetchRecaptchaToken() must exist globally if using reCAPTCHA.
 *
 * NOTES:
 *   • Field tests run in order; first failing test stops validation.
 *   • “required” tests respect user interaction unless running a
 *     full-submit validation (all fields forced to validate).
 *   • Designed to be copied across forms with minimal boilerplate.
 */

// No-jQuery form validation.
const CSS_SUBMITTED = "is-submitted";
const CSS_ERROR = "is-error";

// Simple email regex, not fully RFC822 compliant because that's impractical in JS
// Edge cases can and should be caught by the API instead.
const EmailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function resolveErrorElement(formElement, selector) {
    if (!selector) return null;
    return formElement.querySelector(selector) || null;
}
function setupValidation(formElement, fields, recaptchaId) {
    if (!formElement) {
        console.warn(`Could not find target form element '${formSelector}' for validation setup.`)
    }
    const form = [];

    fields.forEach(({selector, tests, errorSelector}) => {
        const fieldElement = formElement.querySelector(selector);
        if (fieldElement) {
            const field = {
                form: formElement,
                element: fieldElement,
                tests,
                activated: false,
                errorElement: resolveErrorElement(formElement,errorSelector),
                validate: (hard=false) => {
                    const message = validateField(field, hard);
                    if (message === null) {
                        fieldElement.classList.remove(CSS_ERROR);
                        if (field.errorElement !== null) {
                            field.errorElement.classList.add('is-hidden');
                            field.errorElement.innerText = "";
                        }
                        return true;
                    } else {
                        fieldElement.classList.add(CSS_ERROR);
                        if (field.errorElement === null) {
                            field.errorElement = document.createElement('span');
                            field.errorElement.classList.add('AuthRow-error','foo'); // TODO: abstract out the reference to `.AuthRow`
                            if (fieldElement.type === 'checkbox') {
                                fieldElement.parentElement.appendChild(field.errorElement);
                            } else {
                                fieldElement.after(field.errorElement);
                            }
                        }
                        field.errorElement.classList.remove('is-hidden');
                        field.errorElement.innerText = window.PageData.errors[message];
                        return false;
                    }
                }
            }

            fieldElement.addEventListener('change', () => {
                if (fieldElement.value.length > 0) field.activated = true;
                field.validate();
            });
            form.push(field);
        } else {
            console.warn(`Could not find target form field '${selector}' for validation setup.`)
        }
    });
    const RecaptchaElements = formElement.querySelectorAll(".uses-recaptcha");
    async function submitHandler(e) {
        let valid = true;
        for(const field of form) {
            if (!field.validate(true)) {
                valid = false;
            }
        }
        e.preventDefault();
        if (!valid) {
            return;
        }
        if (formElement.classList.contains(CSS_SUBMITTED)) {
            return;
        }
        formElement.classList.add(CSS_SUBMITTED);

        // Handle reCAPTCHA if needed
        if( recaptchaId ) {
            RecaptchaElements.forEach(el => el.disabled = true);
            try {
                await fetchRecaptchaToken(recaptchaId);
                formElement.submit();
            } catch (error) {
                console.error(error);
            }
        } else {
            formElement.submit();
        }
    }
    formElement.addEventListener('submit', submitHandler);
}

function validateField(field, hard=false) {
    if (!field || !field.element || !field.tests) return;

    for (const key in field.tests) {
        const value = field.tests[key];
        if (key === 'required' && value) {
            if (field.element.type === 'checkbox') {
                if (!field.element.checked && (hard || field.activated)) {
                    return 'required';
                }
            } else {
                if (field.element.value.length < 1 && (hard || field.activated)) {
                    return 'required';
                }
            }
        }
        else if (key === 'aliasLength' && value) {
            if (field.element.value.length < 4 && (hard || field.activated)) {
                return 'aliasLength';
            }
        }
        else if (key === 'pwLength' && value) {
            if ((field.element.value.length < 8 || field.element.value.length > 40) && (hard || field.activated)) {
                return 'pwLength';
            }
        }
        else if (key === 'email' && value) {
            const _value = field.element.value;

            // If the field is optional (no `required` test) and empty, don't run email validation.
            const isRequired = !!field.tests.required;

            if (!isRequired && _value.length === 0) {
                continue; // skip this rule; no error
            }

            if (!EmailRegex.test(_value)) {
                return 'email';
            }

        }
        else if (key === 'emailMatch' && value) {
            const TargetField = field.form.querySelector(value);
            if (TargetField) {
                if (TargetField.value !== field.element.value) {
                    return 'emailMatch';
                }
            } else {
                console.error(`Could not find target field '${value}' to match field contents against.`)
            }
        }
        else if (key === 'pwMatch' && value) {
            const TargetField = field.form.querySelector(value);
            if (TargetField) {
                if (TargetField.value !== field.element.value) {
                    return 'pwMatch';
                }
            } else {
                console.error(`Could not find target field '${value}' to match field contents against.`)
            }
        }
        else if (key === 'pwStrength' && value) {
            const password = field.element.value;
            let testsPassed = 0;
            if(/[a-z]/.test(password)) testsPassed++; // has a lowercase letter
            if(/[A-Z]/.test(password)) testsPassed++; // has an uppercase letter
            if(/\d/.test(password)) testsPassed++; // has a digit
            if(/[^\dA-Za-z]/.test(password)) testsPassed++; // has a character that is not a letter or number
            if (testsPassed < 3) {
                return 'pwStrength';
            }
        }
    }

    return null;
}

export { setupValidation };